import React, { useMemo, useState } from "react";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IL_COLORS } from "../../../../constants/investmentLeague";
import SummaryMetrics from "../InvestmentSummary/SummaryMetrics";
import LeagueStandingsTable from "./LeagueStandingsTable";
import TabLoader from "../TabLoader";

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const Leaderboard = ({ data, loading, error, onRetry, onUnitClick }) => {
  const theme = useTheme();
  const t     = theme.custom;
  const [search, setSearch] = useState("");

  const standings = data?.standings ?? [];

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return standings;
    return standings.filter(
      (row) =>
        row.unit.toLowerCase().includes(q) ||
        row.code?.toLowerCase().includes(q)
    );
  }, [standings, search]);

  if (loading) {
    return <TabLoader message="Loading league leaderboard..." />;
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography sx={{ fontSize: "14px", color: t.text2, mb: 2 }}>{error}</Typography>
        {onRetry && (
          <Typography
            component="button"
            onClick={onRetry}
            sx={{
              fontSize:   "13px",
              color:      IL_COLORS.positive,
              cursor:     "pointer",
              background: "none",
              border:     "none",
              fontWeight: 600,
            }}
          >
            Try again
          </Typography>
        )}
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography sx={{ fontSize: "14px", color: t.text3 }}>No leaderboard data available.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "1280px", mx: "auto" }}>
      {/* Page header */}
      <Box
        sx={{
          display:        "flex",
          flexDirection:  { xs: "column", md: "row" },
          alignItems:     { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap:            2,
          mb:             3,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:   { xs: "22px", md: "28px" },
              fontWeight: 700,
              color:      t.text,
              mb:         0.5,
            }}
          >
            Overall Leaderboard
          </Typography>
          <Typography sx={{ fontSize: "13px", color: t.text3 }}>
            League-wide performance across all 11 units — PSIOG Capital Growth League
          </Typography>
        </Box>

        <Box
          sx={{
            px:           1.5,
            py:           0.5,
            borderRadius: "16px",
            background:   "rgba(45,138,91,0.12)",
            border:       `1px solid rgba(45,138,91,0.25)`,
            fontSize:     "11px",
            fontWeight:   600,
            color:        IL_COLORS.positive,
            whiteSpace:   "nowrap",
          }}
        >
          BASE • {data.quarter} • FY 2026-27
        </Box>
      </Box>

      <SummaryMetrics metrics={data.metrics} />

      {/* League standings */}
      <Box
        sx={{
          background:   t.surface,
          border:       `1px solid ${t.border2}`,
          borderRadius: "10px",
          overflow:     "hidden",
        }}
      >
        <Box
          sx={{
            display:        "flex",
            flexDirection:  { xs: "column", sm: "row" },
            alignItems:     { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap:            2,
            px:             { xs: 2, md: 3 },
            py:             2,
            borderBottom:   `1px solid ${t.border}`,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "15px", fontWeight: 600, color: t.text }}>
              Investment League Standings
            </Typography>
            <Typography sx={{ fontSize: "12px", color: t.text3, mt: 0.5 }}>
              {standings.length} units — ranked by All-Time Return
            </Typography>
          </Box>

          <TextField
            size="small"
            placeholder="Search units..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ color: t.text3, display: "flex" }}>
                    <SearchIcon />
                  </Box>
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: "240px" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                fontSize:     "13px",
                background:   t.surface2,
              },
            }}
          />
        </Box>

        <LeagueStandingsTable
          rows={filteredRows}
          onUnitClick={onUnitClick}
        />
      </Box>

      <Typography
        sx={{
          textAlign: "center",
          fontSize:  "11px",
          color:     t.text3,
          mt:        3,
          mb:        1,
        }}
      >
        Click any unit to view its Investment Summary • Rankings update at each quarterly close.
      </Typography>
    </Box>
  );
};

export default Leaderboard;
